import { useMemo } from "react";
import { useMatches } from "react-router-dom";

import { TenantVendorRisk, type User } from "@oneleet/api_client";
import { EVENT_PROPAGATION_DELAY } from "@oneleet/config/constants";

const DEFAULT_REDIRECT = "/";

export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export function waitForEventPropagation(): Promise<void> {
  return sleep(EVENT_PROPAGATION_DELAY);
}

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(
  id: string,
): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(
    () => matchingRoutes.find((route) => route.id === id),
    [matchingRoutes, id],
  );
  return route?.data as Record<string, unknown> | undefined;
}

function isUser(user: unknown): user is User {
  return (
    typeof user === "object" &&
    user !== null &&
    "email" in user &&
    typeof (user as User).email === "string"
  );
}

export function useOptionalUser(): User | undefined {
  const data = useMatchesData("root");
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser(): User {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      "No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.",
    );
  }
  return maybeUser;
}

export function validateEmail(email: unknown): email is string {
  return typeof email === "string" && email.length > 3 && email.includes("@");
}

/**
 * Helper function to join class names together.
 */
export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

/**
 *
 * Map RHF's dirtyFields over the `data` received by `handleSubmit` and return the changed subset of that data.
 * This was taken on from an RHF issue answer https://github.com/orgs/react-hook-form/discussions/1991#discussioncomment-31308
 * @param dirtyFields dirtyFields taken from formState returned by the useForm hook
 * @param values values received by the handleSubmit function
 * @returns Partial allValues that match the dirtyFields
 */
export function getDirtyValues<
  DirtyFields extends Record<string, unknown>,
  Values extends Record<keyof DirtyFields, unknown>,
>(dirtyFields: DirtyFields, values: Values): Partial<typeof values> {
  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    // Unsure when RFH sets this to `false`, but omit the field if so.
    if (!dirtyFields[key]) return prev;

    return {
      ...prev,
      [key]:
        typeof dirtyFields[key] === "object"
          ? getDirtyValues(
              dirtyFields[key] as DirtyFields,
              values[key] as Values,
            )
          : values[key],
    };
  }, {});

  return dirtyValues;
}

export const printByElementId = ({
  id,
  title,
}: {
  id: string;
  title: string;
}) => {
  // Get the element we want to print
  const element = document.getElementById(id);
  if (!element) return;

  // Create a new window
  const printWindow = window.open("", "_blank", "height=600,width=800");

  // Copy the entire head with all styles into the new window
  printWindow?.document.write(`
        <html>
        <head>
            <title>${title}</title>
            ${document.head.innerHTML} <!-- Copy all head elements including styles -->
        </head>
        <body>
        </body>
        </html>
    `);

  // Append the cloned element to the body of the new window
  const clonedElement = element.cloneNode(true);
  printWindow?.document.body.appendChild(clonedElement);

  // Close the document to finish writing
  printWindow?.document.close();

  // Wait for the new window to load, then print and close the window
  if (!printWindow) return;
  printWindow.onload = function () {
    printWindow?.focus();
    printWindow?.print();

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
      // Safari takes some time to open the print window, so delay before closing it
      setTimeout(() => {
        printWindow?.close();
      }, 2000);
    } else {
      printWindow?.close();
    }
  };
};

export function labelForRiskLevel(riskLevel: TenantVendorRisk) {
  switch (riskLevel) {
    case TenantVendorRisk.HIGH:
      return "High";
    case TenantVendorRisk.MEDIUM:
      return "Moderate";
    case TenantVendorRisk.LOW:
      return "Low";
    default:
      return "Unknown";
  }
}
